'use client';

import { useEffect } from "react";
import { deferIfPrerendering } from '@/utils/deferIfPrerendering';

export interface SalesforceProps {
    fetchUrl: string;
    campaignId: string;
    cookieName: string;
}

export default function Salesforce( { fetchUrl, campaignId, cookieName } : SalesforceProps) : React.JSX.Element {
    useEffect(() => {
        const cookies = document.cookie.split(';');

        const anonymousCookie = cookies.find((c) => c.indexOf('SF_ANON_ID') >= 0);
        const anonymousId = anonymousCookie ? anonymousCookie.split('=')[1].trim() : '';

        const experienceCookie = cookies.find((c) => c.indexOf(`SF_EXPID_${cookieName}`) >= 0);
        const experienceId = experienceCookie ? experienceCookie.split('=')[1].trim() : '';

        const abTestCookie = cookies.find((c) => c.trim().startsWith(`${cookieName}=`));
        const abTestVariant = abTestCookie ? abTestCookie.split('=')[1].trim() : '';

        if (!anonymousId || !experienceId || !abTestVariant || abTestVariant === 'EXCLD') return;

        const browserWindow = window as BrowserWindow;

        const sfImpressionEvent = async () => {
            const campaignFlagResponse = await fetch(
                fetchUrl,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        interaction: {
                            name: campaignId,
                        },
                        user: {
                            anonymousId: anonymousId,
                        },
                        campaignStats: [
                            {
                                experienceId: experienceId,
                                stat: 'Impression',
                                control: false,
                            },
                        ],
                        source: {
                            channel: 'Web',
                            url: location.href,
                            urlParams: Object.fromEntries(
                                new URLSearchParams(
                                    window.location.search
                                ).entries()
                            ),
                            urlReferrer: document.referrer,
                        },
                    }),
                }
            );

            const campaignFlagResponseJson = await campaignFlagResponse.json();
            const campaignResponse = campaignFlagResponseJson.campaignResponses[0];

            if (!campaignFlagResponse.ok) {
                return;
            }
            if (!campaignResponse?.payload?.flag) {
                return;
            }

            browserWindow.pushToAdobe(
                {
                    event: 'sfcampaign-dotcom-analytics-serverside-query',
                    salesForceInfoServerSide: {
                        [campaignId]: {
                            statType: 'Query',
                            isControl: campaignResponse?.payload?.isControl,
                            campaignId: campaignResponse?.campaignId,
                            campaignName: campaignResponse?.campaignName,
                            campaignType: campaignResponse?.campaignType,
                            experienceId: campaignResponse?.experienceId,
                            experienceName: campaignResponse?.experienceName,
                            payload: campaignResponse?.payload,
                            state: campaignResponse?.state,
                            templateNames: campaignResponse?.templateNames,
                            type: campaignResponse?.type,
                            userGroup: campaignResponse?.payload?.userGroup,
                        },
                    },
                }
            );

            browserWindow.pushToAdobe(
                {
                    event: 'sfcampaign-dotcom-analytics-serverside-impression',
                    salesForceInfoServerSide: {
                        [campaignId]: {
                            statType: 'Impression',
                            isControl: campaignResponse?.payload?.isControl,
                            campaignId: campaignResponse?.campaignId,
                            campaignName: campaignResponse?.campaignName,
                            campaignType: campaignResponse?.campaignType,
                            experienceId: campaignResponse?.experienceId,
                            experienceName: campaignResponse?.experienceName,
                            payload: campaignResponse?.payload,
                            state: campaignResponse?.state,
                            templateNames: campaignResponse?.templateNames,
                            type: campaignResponse?.type,
                            userGroup: campaignResponse?.payload?.userGroup,
                        },
                    },
                }
            );
        };

        // defer impression until the user actually navigates - a prerender firing a Salesforce
        // impression POST would corrupt A/B test data in the experiment platform
        deferIfPrerendering(sfImpressionEvent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <></>;
}